import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { useHotloadReady } from '@ctw/shared/hooks/use-hotload-ready';
import { tw } from '@ctw/shared/utils/tailwind';
import type { PropsWithChildren } from 'react';

export interface HotloadGateProps extends PropsWithChildren {
  id?: string;
  testId?: string;
  upid?: string;
  /**
   * The FHIR resource types this gate depends on. Only hot-eligible types (see
   * `isHotEligibleResourceType`) can actually gate: the gate unblocks as soon as every *hot-eligible*
   * listed type is present in the patient's `loadedResourceTypes` — without waiting for the rest of
   * the record to finish hotloading. If none of the listed types are hot-eligible (e.g. `Encounter`),
   * hotstorage will never report them, so the gate renders children immediately rather than blocking
   * forever. When omitted (or empty), the gate falls back to blocking on the full `hotloaded` rollup
   * state.
   */
  resourceTypes?: Array<string>;
}

/**
 * Blocks its children behind a loading indicator until the patient's records are ready (see
 * `useHotloadReady`), then renders them. Passes children straight through — never trapping the user
 * on a spinner — when blocking is disabled (`ctw-hotload-blocking` off), there is no UPID, the poll
 * timed out, or the poll hard-errored.
 *
 * Deduped with every other `<HotloadGate>` via the shared `useHotloadStatus` query key, so
 * mounting this gate adds no extra network traffic.
 */
export const HotloadGate = ({ id, testId, upid, resourceTypes, children }: HotloadGateProps) => {
  const ready = useHotloadReady({ upid, resourceTypes });

  if (!ready) {
    return (
      <div id={id} data-testid={testId} className={tw`h-full w-full`}>
        <LoadingSpinner centered={true} message="Loading..." />
      </div>
    );
  }

  return <>{children}</>;
};
